import React, { useEffect, useState } from "react"
import { Formik } from "formik"
import {
    TextInput,
    Select,
    Checkbox,
    Fieldset,
    RadioGroup,
    ErrorSummary,
    FormStatus,
    SubmitButton,
    RequiredNote,
    Revalidate,
    bind,
    summaryErrors,
    isValidEmail,
    parsePhone,
    formStyles as f,
} from "./form"
import styles from "../styles/scss/components/sampleRequestFormNew.module.scss"

// The SEMA 2026 awareness question runs through the show (Nov 3-6, 2026).
// Local Y/M/D args (month is 0-indexed, so 10 = November): it disappears at
// local midnight ending Nov 6. The lambda writes the answer whenever it is
// posted, so a cached bundle submitting after this still lands.
const SEMA_QUESTION_HIDE_AFTER = new Date(2026, 10, 7)

// map incoming DatoCMS records to Select options
const toOptions = (items = []) =>
    (items || []).map(i => ({
        value: i.code || i.slug || i.value || i.id,
        text: i.name || i.title || i.text || i.label || i.slug || i.code,
    }))

export default function SampleRequestFormNew({
    onSubmit,
    fieldsOfWork = [],
    sources = [],
    // Owned by the template, which does the actual request
    submitting = false,
    submitError = false,
}) {
    const [consentFollowup, setConsentFollowup] = useState(false)
    const [consentNewsletter, setConsentNewsletter] = useState(false)
    const [consentSms, setConsentSms] = useState(false)
    const smsConsented = consentSms

    // Hidden by default so the server-rendered markup and the first client
    // render agree; the effect (client-only) reveals it before the cutoff.
    const [showSemaQuestion, setShowSemaQuestion] = useState(false)
    useEffect(() => {
        setShowSemaQuestion(new Date() < SEMA_QUESTION_HIDE_AFTER)
    }, [])

    // "Consent to All" is derived rather than its own piece of state, so
    // unchecking any single box below unchecks it automatically.
    const consentAll = consentFollowup && consentNewsletter && consentSms

    const setAllConsents = checked => {
        setConsentFollowup(checked)
        setConsentNewsletter(checked)
        setConsentSms(checked)
    }

    const fieldOptions = toOptions(fieldsOfWork)
    const sourceOptions = toOptions(sources)
    const defaultSampleSource =
        sourceOptions.length === 1 ? sourceOptions[0].value : ""

    return (
        <div className={styles.container}>
            <h1 className={styles.mainTitle}>
                WD-40® BRAND PRODUCT SAMPLE FORM
            </h1>
            <div className={styles.introContainer}>
                <p>
                    We're thrilled that you want to try a WD-40 Brand product
                    sample. You're just one step away from receiving yours!
                </p>
                <p>
                    Please enter your information and select your opt-in
                    preference to receive our emails. You can unsubscribe at any
                    time. We promise to respect your inbox and only send you
                    content we think you'll find valuable.
                </p>
            </div>
            <h2 className={styles.sectionTitle}>PERSONAL INFORMATION</h2>
            <Formik
                initialValues={{
                    first_name: "",
                    last_name: "",
                    email: "",
                    phone: "",
                    field_of_work: "",
                    sample_source: defaultSampleSource,
                    sema_question_2026: "",
                }}
                validate={values => {
                    const errors = {}
                    if (!values.first_name) errors.first_name = "First Name is required"
                    if (!values.last_name) errors.last_name = "Last Name is required"
                    // The Lambda rejects a bad email too; catching it here (with a
                    // stricter check) avoids a rejection with only a generic error
                    if (!values.email) errors.email = "Email is required"
                    else if (!isValidEmail(values.email)) errors.email = "Invalid email address"
                    const parsedPhone = parsePhone(values.phone)
                    const hasPhoneInput =
                        String(values.phone || "").trim() !== ""
                    if (hasPhoneInput && !parsedPhone)
                        errors.phone =
                            "Please enter a valid phone number, including your country code if you are outside the US (e.g. +44 20 7946 0000)"
                    if (smsConsented) {
                        if (!hasPhoneInput)
                            errors.phone =
                                "Phone Number is required to opt in for SMS messages"
                        else if (parsedPhone && !parsedPhone.isUs)
                            errors.phone =
                                "SMS messages are only available for US phone numbers at this time"
                    }
                    if (!values.field_of_work) errors.field_of_work = "Field of Work is required"
                    if (!values.sample_source) errors.sample_source = "Please select where you received your product sample(s)"
                    return errors
                }}
                onSubmit={(values, { setSubmitting }) => {
                    // Post the number in E.164 (+17606426667) so downstream never
                    // has to guess the country: the lambda's NANP check, the DE's
                    // Phone column and the SMS platform all read it.
                    const parsedPhone = parsePhone(values.phone)
                    const { sema_question_2026, ...rest } = values
                    onSubmit &&
                        onSubmit({
                            ...rest,
                            phone: parsedPhone ? parsedPhone.e164 : "",
                            // Optional: posted only when answered, as a boolean
                            // for the DE's SEMA_question_2026 column
                            ...(showSemaQuestion && sema_question_2026 && {
                                sema_question_2026: sema_question_2026 === "yes",
                            }),
                            consent_followup: consentFollowup,
                            consent_newsletter: consentNewsletter,
                            agree_to_promotional_messages: consentSms,
                            opt_in_confirmation: consentSms,
                            // The Camber & Caster promotion ended 2026-07-19;
                            // the Lambda and DE still have these columns
                            consent_camber_caster: false,
                            consent_camber_caster_sms: false,
                        })
                    setSubmitting(false)
                }}
            >
                {props => (
                    <form className={f.form} onSubmit={props.handleSubmit} noValidate>
                        <RequiredNote />
                        <Revalidate formik={props} on={[smsConsented]} />
                        <ErrorSummary errors={summaryErrors(props)} submitCount={props.submitCount} />
                        <FormStatus status={submitError ? "error" : null} />
                        <div className={f.row}>
                            <TextInput label="First Name" required autoComplete="given-name" {...bind(props, "first_name")} />
                            <TextInput label="Last Name" required autoComplete="family-name" {...bind(props, "last_name")} />
                        </div>
                        <TextInput label="Email" type="email" required autoComplete="email" {...bind(props, "email")} />
                        {/* Free text, not the (999) 999-9999 mask: it has to
                            hold a "+" and international-length numbers.
                            Parsed by parsePhone. */}
                        <TextInput
                            label="Phone Number"
                            type="tel"
                            autoComplete="tel"
                            placeholder="(000) 000-0000 or +44 20 7946 0000"
                            required={smsConsented}
                            help={
                                smsConsented
                                    ? "Required for SMS messages, which are available for US numbers only."
                                    : "US numbers don't need a country code. Outside the US, start with your country code (e.g. +44)."
                            }
                            {...bind(props, "phone")}
                        />
                        {/* Full width: the source label is a sentence and wraps
                            next to a short label, misaligning the two fields */}
                        <Select label="Field of Work" required options={fieldOptions} {...bind(props, "field_of_work")} />
                        {/* One source: it is pre-filled and there is nothing to choose.
                            None (a CMS gap): still shown, so the required error has a
                            field to sit on, as the legacy empty select did */}
                        {sourceOptions.length !== 1 && (
                            <Select
                                label="Select where you receive your product sample(s)"
                                required
                                options={sourceOptions}
                                {...bind(props, "sample_source")}
                            />
                        )}
                        {showSemaQuestion && (
                            <RadioGroup
                                legend="Before seeing it at SEMA, were you aware of the WD-40® Brand x King of the Hill Limited Edition Can promotion?"
                                row
                                options={[{ value: "yes", text: "Yes" }, { value: "no", text: "No" }]}
                                {...bind(props, "sema_question_2026")}
                            />
                        )}
                        <p className={f.sectionIntro}>
                            By completing the information below and clicking
                            Submit, you agree to our{" "}
                            <a
                                href="/privacy/"
                                className={styles.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Privacy Policy
                            </a>{" "}
                            and{" "}
                            <a
                                href="/terms-of-use"
                                className={styles.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Terms of Use
                            </a>{" "}
                            and consent to processing of your information in
                            accordance with our{" "}
                            <a
                                href="/privacy/"
                                className={styles.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Privacy Policy
                            </a>{" "}
                            and{" "}
                            <a
                                href="/terms-of-use"
                                className={styles.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Terms of Use
                            </a>
                            , including to send you our newsletters and
                            marketing communications, and sharing your
                            information with third-party vendors or service
                            providers who support the operation of our business
                            and/or marketing efforts.
                        </p>
                        <p className={styles.thankYouText}>
                            Thanks again for being part of the WD-40 Brand
                            community!
                        </p>
                        <Fieldset id="consents" legend="Your preferences">
                            {/* Same 15px size as the consents it controls; bold
                                so it still reads as the master toggle */}
                            <Checkbox
                                name="consent_all"
                                consent
                                label={<strong>Consent to All</strong>}
                                checked={consentAll}
                                onChange={e => setAllConsents(e.target.checked)}
                            />
                            <Checkbox
                                name="consent_followup"
                                consent
                                label="I agree to allow WD-40® Company to contact me to follow up on my sampling experience."
                                checked={consentFollowup}
                                onChange={e => setConsentFollowup(e.target.checked)}
                            />
                            <Checkbox
                                name="consent_newsletter"
                                consent
                                label="I agree to opt-in to the WD-40 Company email list to receive newsletters and other non-sampling related information from the Company."
                                checked={consentNewsletter}
                                onChange={e => setConsentNewsletter(e.target.checked)}
                            />
                            <Checkbox
                                name="agree_to_promotional_messages"
                                consent
                                checked={consentSms}
                                onChange={e => setConsentSms(e.target.checked)}
                                label={
                                    <>
                                        By checking this box I agree to receive
                                        promotional messages from WD-40 Company.
                                        Message frequency varies. Text HELP to 65254
                                        for help, Text STOP to 65254 to end. Message
                                        and Data Rates May Apply. By opting in, I
                                        authorize WD-40 Company to deliver SMS
                                        messages using an automatic telephone dialing
                                        system and I understand that I am not required
                                        to opt in as a condition of purchasing any
                                        goods or services. By leaving this box
                                        unchecked you will not be opted in for SMS
                                        messages at this time. Click to view our{" "}
                                        <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">
                                            Terms &amp; Conditions
                                        </a>{" "}
                                        or our{" "}
                                        <a href="/privacy/" target="_blank" rel="noopener noreferrer">
                                            Privacy Policy
                                        </a>
                                    </>
                                }
                            />
                        </Fieldset>
                        <div className={f.actions}>
                            <SubmitButton busy={props.isSubmitting || submitting}>Submit</SubmitButton>
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    )
}
